import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, HostListener, inject, OnInit, Signal, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CountableTimeZonePipe, STORAGE_KEYS, StorageHelper } from 'countable@helpers';
import { AttachableInterface, Category, EngTbYearModel, SPIN_APPEND, SPIN_STYLES, SpinInputModel } from 'countable@model';
import { saveAs } from 'file-saver';
import { AvatarModule } from 'ngx-avatars';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, finalize, forkJoin } from 'rxjs';
import { DownloadAttachableInterface } from 'src/app/model/tb/attachable/download-attachable.interface';
import { SpinDirective } from '../../../directives/spin.directive';
import { EngAttachableInterface } from '../../../model/tb/attachable/eng-attachable.interface';
import { ApiService } from '../../../services/api.service';
import { EngService } from '../../../services/eng.service';
import { ExtensionService } from '../../../services/extensions.service';
import { SourceAttachableService } from '../../../services/source-attachable.service';
import { SharedModule } from '../../shared/shared.module';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TbApiService } from '../../../services/tb-api.service';

@Component({
  selector: 'app-source-attachable',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, AvatarModule, MatCheckboxModule, MatMenu, MatMenuItem,
    MatMenuTrigger, NgClass, SharedModule, ReactiveFormsModule, SpinDirective, MatRadioModule,MatDialogModule],
  templateUrl: './source-attachable.component.html',
  styleUrl: './source-attachable.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceAttachableComponent implements OnInit {

  @ViewChild('drawer') drawer: MatDrawer;

  readonly SH: typeof StorageHelper = StorageHelper;
  readonly SK: typeof STORAGE_KEYS = STORAGE_KEYS;
  spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN));
  private spinCount: number = 0;

  originalCategories: WritableSignal<Partial<Category>[]> = signal<Partial<Category>[]>([]);
  filteredCategories: WritableSignal<Partial<Category>[]> = signal<Partial<Category>[]>([])

  searchCategories: WritableSignal<string> = signal<string>('');
  selectedCategories: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());
  selectedYears: WritableSignal<string | null> = signal(null); 
  readonly categoryCount: Signal<number> = computed(() => this.selectedCategories().size);
  readonly selectedYearCount: Signal<number> = computed(() => this.selectedYears() ? 1 : 0);
  categoryCtrl = new FormControl<string>('', { nonNullable: true });
  selectedSortType: WritableSignal<'ASC' | 'DESC' | ''> = signal<'ASC' | 'DESC' | ''>('');
  typeSelectedSortType: WritableSignal<'ASC' | 'DESC' | ''> = signal<'ASC' | 'DESC' | ''>('');
  engagementId: WritableSignal<number> = signal<number>(null);
  engYearDetails: WritableSignal<EngTbYearModel[]> = signal<EngTbYearModel[]>([]);
  originalAttachable: WritableSignal<AttachableInterface[]> = signal<AttachableInterface[]>([]);
  searchAttachable: WritableSignal<string> = signal<string>('');
  selectedAttachable: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
  isShowSelected: WritableSignal<boolean> = signal<boolean>(false);
  totalAttachable: Signal<number> = computed<number>(() => this.attachableList()?.length ?? 0);
  previewUrl: SafeResourceUrl | null = null;
  fileObjectUrl: string = '';
  source: WritableSignal<string>= signal<string>('');
  sourceImage = computed(() => {
    return this.source() === 'Xero' ? '/assets/images/xero/xero-integration.png' : '/assets/images/quickbook/quickbooks-integration.png';
  });
  readonly maxCategorySelections = 5;
  isToggleExpanded: boolean = false; 
  @ViewChild('tbGridSearchInput') tbGridSearchInput;
  isSearchInput: boolean = false;
  categories: Signal<Partial<Category>[]> = computed(() => {
    const search = this.searchCategories()?.toLowerCase()?.trim();
    if (search) {
      return this.originalCategories()?.filter(c => c?.name?.toLowerCase()?.includes(search));
    } else {
      return this.originalCategories();
    }
  });

  attachableList: Signal<Partial<AttachableInterface>[]> = computed(() => {
  let originalAttachable = this.originalAttachable() ?? [];
    if (this.isShowSelected()) {
      originalAttachable = originalAttachable?.filter(c => this.selectedAttachable().has(c?.id));
    }
    const search = this.searchAttachable()?.toLowerCase()?.trim();
    if (search) {
      return originalAttachable?.filter(a => {
        return a?.name?.toLowerCase()?.includes(search) || a?.category?.toLowerCase()?.includes(search) ||
          a?.extension?.toLowerCase()?.includes(search) || a?.fileType?.toLowerCase()?.includes(search);
      });
    } else {
      if (this.selectedAttachableCategories()?.size > 0) {
        originalAttachable = originalAttachable?.filter(a => this.selectedAttachableCategories().has(a?.category));
      }
      if (this.selectedAttachableFileTypes()?.size > 0) {
        originalAttachable = originalAttachable?.filter(a => this.selectedAttachableFileTypes().has(a?.extension));
      }
    }
    if (this.selectedSortType()) {
      if (this.selectedSortType() === 'ASC') {
        originalAttachable = originalAttachable?.sort((a, b) => (a?.fileSize ?? 0) - (b?.fileSize ?? 0));
      } else if (this.selectedSortType() === 'DESC') {
        originalAttachable = originalAttachable?.sort((a, b) => (b?.fileSize ?? 0) - (a?.fileSize ?? 0));
      }
    } else if (this.typeSelectedSortType()) {
      if (this.typeSelectedSortType() === 'ASC') {
        originalAttachable = originalAttachable?.sort((a, b) => (a?.extension ?? '').localeCompare(b?.extension ?? ''));
      } else if (this.typeSelectedSortType() === 'DESC') {
        originalAttachable = originalAttachable?.sort((a, b) => (b?.extension ?? '').localeCompare(a?.extension ?? ''));
      }
    }
    return originalAttachable;
  });

  attachableCategories: Signal<string[]> = computed(() => {
    const cat = new Set<string>(this.originalAttachable()?.map(a => a?.category));
    return Array.from(cat);
  });
  selectedAttachableCategories: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());
  attachableFileTypes: Signal<string[]> = computed(() => {
    const types = new Set<string>(this.originalAttachable()?.map(a => a?.extension));
    return Array.from(types);
  });
  selectedAttachableFileTypes: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  engagementInfo: WritableSignal<Record<string, string>> = signal<Record<string, string>>(null);
  isAllSelected: Signal<boolean> = computed(() => {
    return this.selectedAttachable()?.size > 0 && this.selectedAttachable()?.size === this.attachableList()?.length;
  });
  isFormSubmit: WritableSignal<boolean> = signal<boolean>(false);

  initialSelectedCategories: WritableSignal<Set<string>> = signal(new Set(this.selectedCategories()));
  initialSelectedYears: WritableSignal<string | null> = signal(this.selectedYears())
  isSubmitDisabled = computed(() => {
  return this.categoryCount() === 0 || !this.selectedYears();
});

  periodMinDate: WritableSignal<string> = signal("");
  periodMaxDate: WritableSignal<string> = signal("");
  isDateDisabled = computed(() => {
    return this.periodMinDate() === "" || this.periodMaxDate() === "";
  });

  selectedStartDate: WritableSignal<string> = signal<string>('');
  selectedEndDate: WritableSignal<string> = signal<string>('');

  private destroyRef = inject(DestroyRef);
  fileType: WritableSignal<string> = signal<string>('');
  isPreviewableFile: WritableSignal<boolean> = signal<boolean>(false);
  currentBlob: Blob | null = null;
  currentFileName: string = '';
  @ViewChild('errorDialog') errorDialog: TemplateRef<any>;
  errorMessage: WritableSignal<string> = signal<string>('');
  windowWidth=1600;
  @HostListener('window:resize', ['$event'])
      onResize(event: Event): void {
        this.windowWidth = (event.target as Window).innerWidth;
        // Perform any other actions needed on resize
        console.log('Window resized:', this.windowWidth, 'x');
      }
      
  constructor(private api: SourceAttachableService, private router: ActivatedRoute, private apiService: ApiService,
              private sanitizer: DomSanitizer, private extensionService: ExtensionService,  private timeZonePipe: CountableTimeZonePipe,
              private toaster: ToastrService, private cdr: ChangeDetectorRef, private engService: EngService, private dialog: MatDialog) {
    this.engagementInfo.set(this.getEngagementInfo());
  }

  ngOnInit(): void {
    this.router.paramMap.pipe().subscribe(params => {
      if (params.has('engagementId') && params.get('engagementId')) {
        this.engagementId.set(parseInt(params.get('engagementId')));
        this.getCategoriesAndTypes();
        this.getEngYearDetails();
      }
      if(params.has('source') && params.get('source')){
        this.source.set(params.get('source'));
      }
    });
    this.categoryCtrl.valueChanges
    .pipe(debounceTime(200), distinctUntilChanged())
    .subscribe((term) => {
      const normalized = term?.toLowerCase().trim() || '';
      const filtered = normalized
        ? this.originalCategories().filter((c) => c?.name?.toLowerCase().includes(normalized))
        : this.originalCategories();
      this.filteredCategories.set(filtered);
    });
  }

  private getCategoriesAndTypes() {
    this.updateSpinnerConfig(true);
    this.api.fetchAttachableCategoryAndTypes(this.engagementId())
      .pipe(
        finalize(() => this.updateSpinnerConfig(false))
      ).subscribe(res => {
      if (res?.status === 200 && res?.data?.categories?.length) {
        this.originalCategories.set(res?.data?.categories);
        this.filteredCategories.set(res?.data?.categories);
      }
    });
  }

  private getEngYearDetails() {
    this.updateSpinnerConfig(true);
    this.engService.getEngagementYearDetails(this.engagementId())
      .pipe(
        finalize(() => this.updateSpinnerConfig(false))
      ).subscribe({
      next: response => {
        if (response?.length > 0) {
            const engYearDetails = response?.map((r:EngTbYearModel) => ({...r, acctdate: this.timeZonePipe.transform(r.acctdate, 'MMM d yyyy', 'UTC')}));
            this.engYearDetails.set(engYearDetails);
          }
      }
    });
  }

  toggleCategory(value: string) {
    const set = new Set(this.selectedCategories());
    set.has(value) ? set.delete(value) : set.add(value);
    this.selectedCategories.set(set);
  }

  toggleYearType(value: string) {
    this.selectedYears.set(value);
    const startDate = this.engService.checkLeapYear(value, 0, true);
    const endDate = this.engService.checkLeapYear(value, 0, false);
    this.periodMinDate.set(startDate);
    this.periodMaxDate.set(endDate);
    this.selectedStartDate.set(startDate);
    this.selectedEndDate.set(endDate);
    this.cdr.markForCheck();
  }

  formatToYYYYMMDD(input: string): string {
    const date = new Date(input);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  getSelectedYearsDateRangeNew(
    data: EngTbYearModel[],
    selectedYears: string[]
  ): { startDate: string; endDate: string } {
    if (!selectedYears || selectedYears.length === 0) {
      return {
        startDate: '',
        endDate: '',
      };
    }
    const selectedYear = selectedYears[0];
    const sorted = [...data].sort((a, b) => parseInt(b.acctyear) - parseInt(a.acctyear));

    const selectedEntry = sorted.find(d => d.acctyear === selectedYear);
    const selectedIndex = sorted.findIndex(d => d.acctyear === selectedYear);
    const previousEntry = sorted[selectedIndex + 1];

    const endDate = this.timeZonePipe.transform(selectedEntry?.acctdate || '', 'YYYY-MM-DD');

    let startDate = '';
    if (previousEntry?.acctdate) {
      const previousDate = new Date(previousEntry.acctdate);
      previousDate.setDate(previousDate.getDate() + 1);
      startDate = this.formatToYYYYMMDD(previousDate.toISOString());
    } else {
      // fallback: subtract 1 year from selected acctdate and add 1 day
    const selectedAcctDate = new Date(selectedEntry?.acctdate || '');
    selectedAcctDate.setFullYear(selectedAcctDate.getFullYear() - 1);
    selectedAcctDate.setDate(selectedAcctDate.getDate() + 1);
    startDate = this.formatToYYYYMMDD(selectedAcctDate.toISOString());
    }
    return { startDate, endDate };
  }


  toggleAttachable(value: string) {
    const set = new Set(this.selectedAttachable());
    set.has(value) ? set.delete(value) : set.add(value);
    this.selectedAttachable.set(set);
  }


  getSelectedAttachable() {
    this.isFormSubmit.set(true);
    this.initialSelectedCategories.set(new Set(this.selectedCategories()));
    this.initialSelectedYears.set(this.selectedYears());
    this.originalAttachable.set([]);
    this.selectedAttachable.set(new Set());
      this.updateSpinnerConfig(true);
      this.api.fetchSelectedAttachable({
        categories: Array.from(this.selectedCategories()),
        fileTypes: [],
        endDate: this.selectedEndDate(),
        startDate: this.selectedStartDate(),
        engagementId: this.engagementId()
      }).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
        next: (res) => {
        if (res?.status === 200 && res?.data?.length > 0) {
          this.originalAttachable.set(this.updateFileTypeAndFilesize(res.data));
        } else if(res?.status === 429) {
          this.errorMessage.set(res?.errors[0]);
          this.showErrorDialogue();
        } else{
          this.originalAttachable.set([]);
          if(res.errors) {
            res.errors.forEach((error: string) => this.toaster.error(error));
          }
        }
          this.cdr.markForCheck();
        }, error: (err) => {
          this.originalAttachable.set([]);
          this.cdr.markForCheck();
        }, complete: () => {
          console.log('Completed');
        }
      });
  }

  private getListForSeparateYears() {
    this.originalAttachable.set([]);
    const {cy, py} = this.getSeparateYears(Array.from(this.selectedYears()));
    const currentYear = this.getSelectedYearsDateRange(this.engYearDetails(), [cy.toString()]);
    const apiA = this.api.fetchSelectedAttachable({
      categories: Array.from(this.selectedCategories()),
      fileTypes: [],
      endDate: currentYear?.endDate,
      startDate: currentYear?.startDate,
      engagementId: this.engagementId()
    });
    const previousYear = this.getSelectedYearsDateRange(this.engYearDetails(), [py.toString()]);
    const apiB = this.api.fetchSelectedAttachable({
      categories: Array.from(this.selectedCategories()),
      fileTypes: [],
      endDate: previousYear?.endDate,
      startDate: previousYear?.startDate,
      engagementId: this.engagementId()
    });
    this.updateSpinnerConfig(true);
    forkJoin([apiA, apiB]).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
      next: (res) => {
        const [resA, resB] = res;
        let combinedData: AttachableInterface[] = [];

        if (resA?.status === 200 && resA?.data?.length) {
          combinedData = [...combinedData, ...this.updateFileTypeAndFilesize(resA.data)];
        } else {
          if(resA.errors) {
            resA.errors.forEach((error: string) => this.toaster.error(error));
          }
        }

        if (resB?.status === 200 && resB?.data?.length) {
          combinedData = [...combinedData, ...this.updateFileTypeAndFilesize(resB.data)];
        } else {
          if(resB.errors) {
            resB.errors.forEach((error: string) => this.toaster.error(error));
          }
        }

        this.originalAttachable.set(combinedData);
        this.cdr.markForCheck();
      },
      error: (err) => {
      console.error('Error in separate years fetch:', err);
      this.originalAttachable.set([]);
      this.cdr.markForCheck();
    },
    complete: () => {
          console.log('Completed');
    }
    });
  }

  clearCategories() {
    this.selectedCategories.set(new Set());
    this.filteredCategories.set(this.originalCategories());
    this.categoryCtrl.setValue('');
  }

  selectAllCategories() {
    const allCategories = new Set(this.filteredCategories().map(cat => cat.value));
    this.selectedCategories.set(allCategories);
  }

  getAttachableById(id: string) {
    this.revokeUrl();
    this.updateSpinnerConfig(true);
    this.api.fetchAttachableById(id, this.engagementId())
    .pipe(finalize(() => this.updateSpinnerConfig(false)))
    .subscribe({
      next: (blob: Blob) => {
        if (blob.size === 0) {
          return;
        }

        const mimeType = blob.type || 'application/pdf';
        if (!mimeType.includes('pdf') && !mimeType.startsWith('image/')) {
          blob.text().then(text => {
            console.log('Error', text);
          });
          return;
        }

        this.fileObjectUrl = URL.createObjectURL(blob);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.fileObjectUrl);
      }
    });
  }

  previewAttachable(row: Partial<AttachableInterface>) {
    const categoryMap = new Map(
    this.originalCategories().map(cat => [cat.name, cat.value])
    );
    const mappedCategory = categoryMap.get(row?.category) ?? row?.category;
    const input: DownloadAttachableInterface = {
      engagementId: this.engagementId(),
      attachableId: row?.id,
      fileType: row?.fileType,
      accountId: row?.accountId,
      category:mappedCategory,
      fileName: row?.name
    };
    this.updateSpinnerConfig(true);
    this.api.downloadAttachableByFileIds(input)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.updateSpinnerConfig(false)))
      .subscribe({
        next: async (blob: Blob) => {
          try {
            if (blob.size === 0) {
              throw new Error('Empty file received');
            }
            if (blob.type === 'text/xml') {
              const content = await blob.text();
              if (content.includes('<Error>')) {
                console.error('Received XML error instead of file:', content);
                this.previewUrl = null;
                return;
              }
            }
            const fileName = row?.name || '';
            const extMatch = fileName.split('.').pop()?.toLowerCase() || 'unknown';
            this.fileType.set(extMatch);
            this.isPreviewableFile.set(this.extensionService.isPreviewableFile(extMatch));
            const adjustedBlob = this.extensionService.adjustBlobTypeBasedOnExtension(blob, extMatch);
            const url = URL.createObjectURL(adjustedBlob);
            this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          } catch (error) {
            console.error('Error creating preview URL:', error);
          }
          if (this.drawer) {
            this.drawer.open();
          }
          this.currentBlob = blob;
          this.currentFileName = row?.name;
        },
        error: (err) => {
          console.error('File fetch error', err);
          this.previewUrl = null;
        }
      });
  }

  downloadBlob() {
    if (!this.currentBlob || !this.currentFileName) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(this.currentBlob);
    link.download = this.currentFileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  saveToEngagement() {
    const categoryMap = new Map(
      this.originalCategories().map(cat => [cat.name, cat.value])
    );

    let attachables = this.attachableList()
      ?.filter(a => this.selectedAttachable().has(a?.id))
      .map(a => {
        const matchedCategoryName = categoryMap.get(a?.category);
        return {
          ...a,
          categoryKey: matchedCategoryName ?? a?.category
        };
      });
    const input: EngAttachableInterface = {
      engagementId: this.engagementId(),
      attachableIds: attachables?.map(a => {
        return { attachableId: a?.id, fileType: a?.extension, accountId: a?.accountId, category: a?.categoryKey, fileName: a?.name };
      })
    };
    this.updateSpinnerConfig(true);
    this.api.saveAttachableByIds(input).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
      next: res => {
        if (res?.status === 200) {
          this.toaster.success('Files saved successfully to document section');
          window.opener?.postMessage({ action: 'refreshDocuments' }, '*');
        }
      }
    });
  }

  downloadToComputer() {
    const categoryMap = new Map(
      this.originalCategories().map(cat => [cat.name, cat.value])
    );

    let attachables = this.attachableList()
      ?.filter(a => this.selectedAttachable().has(a?.id))
      .map(a => {
        const matchedCategoryName = categoryMap.get(a?.category);
        return {
          ...a,
          categoryKey: matchedCategoryName ?? a?.category
        };
      });
    const input: EngAttachableInterface = {
      engagementId: this.engagementId(),
      attachableIds: attachables?.map(a => {
        return { attachableId: a?.id, fileType: a?.extension, accountId: a?.accountId, category: a?.categoryKey, fileName: a?.name };
      })
    };
    this.updateSpinnerConfig(true);
    this.api.downloadAttachableByIds(input).pipe(finalize(() => this.updateSpinnerConfig(false)))
      .subscribe({
        next: res => {
          if (res) {
            saveAs(res, 'attachable.zip');
          }
        }
      });
  }

  loadOtherDocuments(filePath: string) {
    this.apiService.getFiles(filePath).subscribe({
      next: res => {
        // this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(res));
      }, error: err => {
        console.error('Error fetching file URL: ', err);
      }
    });
  }

  private revokeUrl() {
    if (this.fileObjectUrl) {
      URL.revokeObjectURL(this.fileObjectUrl);
      this.fileObjectUrl = null;
    }
  }

  getEngagementInfo(): Record<string, string> {
    const engagement = this.SH.getItem(this.SK.ENGAGEMENT_DETAILS);
    return {
      'engagementName': engagement?.engagementName,
      'engType': 'COM',
      'clientName': engagement?.clientName,
      'yearEnd': engagement?.yearEnd
    };
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    this.spinnerConfig.update((config) => ({...config, isSpinShown: this.spinCount > 0}));
  }

  getSelectedYearsDateRange(
    data: EngTbYearModel[],
    selectedYears: string[]
  ): { startDate: string; endDate: string; isOddSelection: boolean } {
    const sorted = [...data].sort((a, b) => parseInt(b.acctyear) - parseInt(a.acctyear));
    const allYears = sorted.map((d) => d.acctyear);
    const currentYear = allYears[0];

    const formatDate = (year: string, month: number, day: number) =>
      `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    if (!selectedYears || selectedYears.length === 0) {
      const start = allYears[2] ?? allYears[allYears.length - 1];
      return {
        startDate: formatDate(start, 1, 1),
        endDate: formatDate(currentYear, 12, 31),
        isOddSelection: false,
      };
    }

    // Check for odd selection (non-consecutive)
    const selectedNums = selectedYears.map((y) => parseInt(y)).sort((a, b) => a - b);
    let isOddSelection = false;
    for (let i = 1; i < selectedNums.length; i++) {
      if (selectedNums[i] !== selectedNums[i - 1] + 1) {
        isOddSelection = true;
        break;
      }
    }

    if (isOddSelection) {
      return {
        startDate: null,
        endDate: null,
        isOddSelection: true,
      };
    }

    const start = Math.min(...selectedNums).toString();
    const end = Math.max(...selectedNums).toString();

    return {
      startDate: formatDate(start, 1, 1),
      endDate: formatDate(end, 12, 31),
      isOddSelection: false,
    };
  }


  private getSeparateYears(years: string[]) {
    let list = years?.map(y => +y);
    list = list.sort((a, b) => b - a);
    return {cy: list[0], py: list[1]};
  }

  searchInCategories(event: KeyboardEvent) {
    const value = (event.target as HTMLInputElement).value?.toLowerCase().trim();
    this.searchCategories.set(value); // optional for tracking
    const filtered = value
      ? this.originalCategories().filter(c => c?.name?.toLowerCase().includes(value))
      : this.originalCategories();
    this.filteredCategories.set(filtered); // <- manually update
  }

  searchInAttachable(event: KeyboardEvent) {
    const search = event?.target as HTMLInputElement;
    this.searchAttachable.set(search?.value);
  }

  showSelectedAttachable() {

  }

  toggleAllAttachable(event: MatCheckboxChange) {
    this.selectedAttachable.set(event?.checked ? new Set(this.attachableList()?.map(a => a?.id)) : new Set());
  }

  toggleAttachableCategory(value: string) {
    const set = new Set(this.selectedAttachableCategories());
    set.has(value) ? set.delete(value) : set.add(value);
    this.selectedAttachableCategories.set(set);
  }

  clearAttachableCategories() {
    this.selectedAttachableCategories.set(new Set());
  }

  clearAttachableFileType() {
    this.selectedAttachableFileTypes.set(new Set());
  }

  toggleFileType(value: string) {
    const set = new Set(this.selectedAttachableFileTypes());
    set.has(value) ? set.delete(value) : set.add(value);
    this.selectedAttachableFileTypes.set(set);
  }

  sortAttachable(sortType: 'ASC' | 'DESC') {
    this.selectedSortType.set(sortType);
    this.typeSelectedSortType.set('');
  }

  sortAttachableType(sortType: 'ASC' | 'DESC') {
    this.typeSelectedSortType.set(sortType);
    this.selectedSortType.set('');
  }

  updateFileTypeAndFilesize(list: AttachableInterface[]): AttachableInterface[] {
    list?.forEach(a => {
      a.extension = this.extensionService.getExtensionByMimeType(a?.fileType);
      a.size = this.formatFileSize(a?.fileSize);
    });
    return list;
  }

  formatFileSize(bytes: number): string {
    const kilobytes = bytes / 1024;

    if (kilobytes > 1024) {
      const megabytes = kilobytes / 1024;
      return `${megabytes.toFixed(2)} MB`;
    } else {
      return `${kilobytes.toFixed(2)} KB`;
    }
  }

  private setsAreEqual<T>(a: Set<T>, b: Set<T>): boolean {
    if (a.size !== b.size) return false;
    for (let item of a) if (!b.has(item)) return false;
    return true;
  }

  toggleDrwaer(){
    this.isToggleExpanded = !this.isToggleExpanded;
  }
  setFocus() {
    this.isSearchInput = true;
    this.tbGridSearchInput.nativeElement.focus();
  }

  clearSearchField() {
    this.isSearchInput = false;
    this.tbGridSearchInput.nativeElement.value = '';
  }

  onChangeEndDate(event: any) {
    this.selectedEndDate.set(this.timeZonePipe.transform(event, 'YYYY-MM-DD', 'UTC'));  
  }

  onChangeStartDate(event: any) {
    this.selectedStartDate.set(this.timeZonePipe.transform(event, 'YYYY-MM-DD', 'UTC'));
  }

  showErrorDialogue(){
    this.dialog.open(this.errorDialog, {
      width: '400px',
    });
  }
  
}
